import type { Timesheet, Employee, Project } from '@/types/timesheet';
import type { TimesheetReportEntry } from '@/types/timesheetReport';
import { rejectionService } from '@/services/rejectionService';

export function transformTimesheetsToReportEntries(
  timesheets: Timesheet[],
  employees: Employee[],
  projects: Project[]
): TimesheetReportEntry[] {
  const reportEntries: TimesheetReportEntry[] = [];

  timesheets.forEach((timesheet) => {
    const employee = employees.find((e) => e.id === timesheet.employeeId);
    if (!employee) return;

    // Get rejection history for this timesheet
    const rejectionHistory = rejectionService.getTimesheetRejectionHistory(timesheet.id);

    timesheet.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      if (!project) return;

      const costCentre = project.costCentres.find((cc) => cc.id === entry.costCentreId);
      if (!costCentre) return;

      const totalHours =
        entry.hours.monday +
        entry.hours.tuesday +
        entry.hours.wednesday +
        entry.hours.thursday +
        entry.hours.friday +
        entry.hours.saturday +
        entry.hours.sunday;

      reportEntries.push({
        id: `${timesheet.id}-${entry.id}`,
        weekEnding: timesheet.weekEnding,
        employeeId: employee.id,
        employeeName: employee.name,
        department: employee.department,
        projectCode: project.code,
        projectName: project.name,
        costCentreCode: costCentre.code,
        costCentreName: costCentre.name,
        description: entry.description,
        dailyNotes: entry.dailyNotes,
        mondayHours: entry.hours.monday,
        tuesdayHours: entry.hours.tuesday,
        wednesdayHours: entry.hours.wednesday,
        thursdayHours: entry.hours.thursday,
        fridayHours: entry.hours.friday,
        saturdayHours: entry.hours.saturday,
        sundayHours: entry.hours.sunday,
        totalHours,
        status: timesheet.status,
        submittedAt: timesheet.submittedAt,
        approvedAt: timesheet.approvedAt,
        rejectedAt: timesheet.rejectedAt,
        approverName: timesheet.approverName,
        billable: project.billable,
        hasRejections: rejectionHistory.rejections.length > 0,
        rejectionCount: rejectionHistory.rejections.length,
        isOvertime: !!entry.overtimeTypeId,
        overtimeTypeName: entry.overtimeTypeName,
        overtimeCostMultiplier: entry.overtimeCostMultiplier,
        overtimeBillMultiplier: entry.overtimeBillMultiplier,
        hoursToComplete: entry.hoursToComplete ?? null,
      });
    });
  });

  return reportEntries;
}

export function getReportSummary(entries: TimesheetReportEntry[]) {
  const totalHours = entries.reduce((sum, entry) => sum + entry.totalHours, 0);
  const billableHours = entries
    .filter((entry) => entry.billable)
    .reduce((sum, entry) => sum + entry.totalHours, 0);
  const nonBillableHours = totalHours - billableHours;

  const statusCounts = entries.reduce(
    (acc, entry) => {
      acc[entry.status] = (acc[entry.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const uniqueEmployees = new Set(entries.map((entry) => entry.employeeId)).size;
  const uniqueProjects = new Set(entries.map((entry) => entry.projectCode)).size;
  const entriesWithRejections = entries.filter((entry) => entry.hasRejections).length;

  return {
    totalHours,
    billableHours,
    nonBillableHours,
    billablePercentage: totalHours > 0 ? (billableHours / totalHours) * 100 : 0,
    statusCounts,
    uniqueEmployees,
    uniqueProjects,
    entriesWithRejections,
    totalEntries: entries.length,
  };
}
